import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { confirmPasswordResetAPI } from '../api/auth';
import Logo from '../components/ui/Logo';

export default function ReimpostaPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Token assente o ritenuto non più valido dal backend.
  const [invalidToken, setInvalidToken] = useState(!token);

  function validateForm(): string[] {
    const errors: string[] = [];
    if (password.length < 8) errors.push(t('auth.validazione.password_min'));
    if (!/[A-Z]/.test(password)) errors.push(t('auth.validazione.password_maiuscola'));
    if (!/[a-z]/.test(password)) errors.push(t('auth.validazione.password_minuscola'));
    if (!/[0-9]/.test(password)) errors.push(t('auth.validazione.password_numero'));
    if (password !== confirmPassword) errors.push(t('auth.validazione.password_non_corrispondono'));
    return errors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors([]);
    setSubmitting(true);

    try {
      await confirmPasswordResetAPI(token, password);
      navigate('/login', {
        replace: true,
        state: { successMessage: t('auth.reset.password_reimpostata') },
      });
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          // Violazioni regole password (validazione pydantic).
          setFormErrors(detail.map((d: { msg: string }) => d.msg));
        } else {
          // Token invalido/scaduto/usato: messaggio stringa dal backend.
          setInvalidToken(true);
        }
      } else {
        setFormErrors([t('auth.reset.errore_generico')]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-stone-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-md border border-stone-mid overflow-hidden">
          <div className="flex justify-center py-6 border-b border-stone-mid px-6">
            <Logo width={140} />
          </div>

          {invalidToken ? (
            <div className="px-6 py-8 text-center">
              <p className="text-lg font-semibold text-ink-dark mb-2">
                {t('auth.reset.link_non_valido_titolo')}
              </p>
              <p className="text-sm text-ink-soft mb-6">
                {t('auth.reset.link_non_valido_testo')}
              </p>
              <a
                href="/password-dimenticata"
                className="inline-block px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
              >
                {t('auth.reset.richiedi_nuovo_link')}
              </a>
            </div>
          ) : (
            <div className="px-6 py-6">
              <h1 className="text-lg font-semibold text-ink-dark mb-1">
                {t('auth.reset.imposta_titolo')}
              </h1>
              <p className="text-sm text-ink-soft mb-5">
                {t('auth.reset.imposta_sottotitolo')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.reset.nuova_password')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                  <p className="mt-1 text-xs text-ink-soft">
                    {t('auth.reset.requisiti_password')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-dark mb-1">
                    {t('auth.reset.conferma_password')} <span className="text-accent-danger">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
                  />
                </div>

                {formErrors.length > 0 && (
                  <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger space-y-1">
                    {formErrors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 px-4 text-sm font-semibold text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? t('auth.reset.reimpostazione_in_corso') : t('auth.reset.reimposta')}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
